import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { showSuccess, showError } from '@/lib/swal';
import { QuestionnaireItem, Course, UnitType, TicketType } from '@/types';
import { Plus, Trash2 } from 'lucide-react';
import { generateId } from '@/lib/utils';
import { normaliseCustomPrefixesForSave } from '@/lib/rank-utils';
import { TagInput } from '@/components/ui/tag-input';
import { MAX_CREDIT_BOOKING_FEE_PENCE } from '@/lib/platform-booking-fee';
import { VenueSelector } from '@/components/venues/VenueSelector';
import { TicketMode, TICKET_MODE_DESCRIPTIONS } from '@/lib/ticket-mode';

export default function CreateEventPage() {
  const { unitId, lodgeId } = useParams<{ unitId?: string; lodgeId?: string }>();
  const effectiveId = unitId || lodgeId; // Support both unitId and lodgeId
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [eventName, setEventName] = useState('');
  const [ceremonyType, setCeremonyType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('19:00');
  const [venueId, setVenueId] = useState('');
  const [location, setLocation] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [dresscode, setDresscode] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireItem[]>([]);
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [ticketMode, setTicketMode] = useState<TicketMode>('mealChoice');
  const [isPublic, setIsPublic] = useState(false);
  const [unitEntityName, setUnitEntityName] = useState('Unit');
  const [requirePrefixRankUnitInfo, setRequirePrefixRankUnitInfo] = useState(true);
  const [allowFutureEventsInterest, setAllowFutureEventsInterest] = useState(true);
  const [useCustomPrefixes, setUseCustomPrefixes] = useState(false);
  const [customPrefixes, setCustomPrefixes] = useState<string[]>([]);
  const [guestCoversCreditCost, setGuestCoversCreditCost] = useState(false);
  const [creditBookingFeePoundsInput, setCreditBookingFeePoundsInput] = useState('0.60');
  const [loading, setLoading] = useState(false);
  const [availableCeremonies, setAvailableCeremonies] = useState<string[]>([]);

  const unitInfoLabel = unitEntityName || 'Unit';

  // Fetch available ceremonies based on unit type
  useEffect(() => {
    const fetchUnitAndCeremonies = async () => {
      if (!effectiveId) return;

      try {
        // Fetch the unit/unit to get its typeId
        const unitDoc = await getDoc(doc(db, 'units', effectiveId));
        if (!unitDoc.exists()) return;

        const unitData = unitDoc.data();
        const typeId = unitData.typeId;
        let derivedEntityName = unitData.entityName || 'Unit';

        if (!typeId) return;

        // Fetch system settings to get unit types
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (!settingsDoc.exists()) return;

        const settings = settingsDoc.data();
        const unitTypes = settings.unitTypes as UnitType[] | undefined;

        if (!unitTypes) return;

        // Find the matching unit type
        const unitType = unitTypes.find((ut) => ut.id === typeId);
        if (unitType && unitType.ceremonies) {
          setAvailableCeremonies(unitType.ceremonies);
        }
        if (unitType?.entityName) {
          derivedEntityName = unitType.entityName;
        }

        setUnitEntityName(derivedEntityName);
      } catch (error) {
        console.error('Error fetching ceremonies:', error);
      }
    };

    fetchUnitAndCeremonies();
  }, [effectiveId]);

  const addQuestion = () => {
    setQuestionnaire([
      ...questionnaire,
      {
        id: generateId(),
        question: '',
        type: 'text',
        required: false,
      },
    ]);
  };

  const removeQuestion = (id: string) => {
    setQuestionnaire(questionnaire.filter((q) => q.id !== id));
  };

  const updateQuestion = (id: string, field: keyof QuestionnaireItem, value: any) => {
    setQuestionnaire(
      questionnaire.map((q) => (q.id === id ? { ...q, [field]: value } : q))
    );
  };

  // Ticket configuration handlers
  const addTicket = () => {
    setTickets([
      ...tickets,
      {
        id: generateId(),
        name: '',
        price: 0,
        totalAvailable: undefined,
        restricted: false,
        courses: [],
      },
    ]);
  };

  const removeTicket = (id: string) => {
    setTickets(tickets.filter((t) => t.id !== id));
  };

  const updateTicketField = (id: string, field: keyof TicketType, value: any) => {
    setTickets(tickets.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const addCourseToTicket = (ticketId: string) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId
          ? { ...t, courses: [ ...(t.courses || []), { id: generateId(), name: '', options: [] } ] }
          : t
      )
    );
  };

  const removeCourseFromTicket = (ticketId: string, courseId: string) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId ? { ...t, courses: (t.courses || []).filter((c) => c.id !== courseId) } : t
      )
    );
  };

  const updateTicketCourse = (ticketId: string, courseId: string, field: keyof Course, value: any) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              courses: (t.courses || []).map((c) => (c.id === courseId ? { ...c, [field]: value } : c)),
            }
          : t
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!effectiveId) {
      showError('Unit/Unit ID is missing');
      return;
    }

    if (!currentUser) {
      showError('You must be logged in to create an event');
      return;
    }

    // Combine date and time
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);

    setLoading(true);
    try {
      let creditBookingFeePence: number | undefined;
      if (guestCoversCreditCost) {
        const normalized = creditBookingFeePoundsInput.trim().replace(/,/g, '');
        const pounds = Number.parseFloat(normalized);
        if (!Number.isFinite(pounds) || pounds < 0 || Math.round(pounds * 100) > MAX_CREDIT_BOOKING_FEE_PENCE) {
          showError('Enter a booking fee between £0.00 and £5.00.');
          setLoading(false);
          return;
        }
        creditBookingFeePence = Math.round(pounds * 100);
      }

      const defaultRsvpDeadline = new Date(eventDateTime.getTime() - 7 * 24 * 60 * 60 * 1000);
      const paymentDate = new Date(eventDateTime);
      const eventData = {
        name: eventName,
        ceremonyType: ceremonyType && ceremonyType !== 'none' ? ceremonyType : null,
        date: eventDateTime,
        location,
        venueId: venueId && venueId !== '__custom__' ? venueId : null,
        questionnaire,
        ticketMode,
        tickets: tickets
          .map((t) => ({
            ...t,
            name: (t.name || '').trim(),
            price: Number(t.price) || 0,
            totalAvailable: t.totalAvailable ? Number(t.totalAvailable) : null,
            restricted: Boolean(t.restricted),
            courses: ticketMode === 'multiTenant'
              ? []
              : (t.courses || []).filter((c) => c.name && c.options && c.options.length > 0),
          }))
          .filter((t) => t.name),
        maxCapacity: maxCapacity ? parseInt(maxCapacity) : null,
        currentAttendees: 0,
        waitlistEnabled: !!maxCapacity,
        waitlist: [],
        rsvpDeadline: rsvpDeadline ? new Date(rsvpDeadline) : defaultRsvpDeadline,
        paymentDeadline: paymentDate,
        eventStatus: 'draft',
        dresscode,
        customInstructions,
        isPublic,
        requirePrefixRankUnitInfo,
        allowFutureEventsInterest,
        useCustomPrefixes,
        customPrefixes: normaliseCustomPrefixesForSave(customPrefixes),
        guestCoversCreditCost,
        ...(guestCoversCreditCost && creditBookingFeePence !== undefined
          ? { creditBookingFeePence }
          : {}),
        createdBy: currentUser.uid,
        createdAt: new Date(),
      };

      const docRef = await addDoc(collection(db, 'units', effectiveId, 'events'), eventData);

      showSuccess('Event created successfully!');
      navigate(`/event/${docRef.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      showError('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <DashboardContentContainer className="max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate(unitId ? `/unit/${unitId}/dashboard` : `/unit/${unitId}/dashboard`)}>
              ← Back to {unitId ? 'Unit' : 'Unit'} Dashboard
            </Button>
          </div>

          <h1 className="mb-8 text-3xl font-bold">Create New Event</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Essential details about your event</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="eventName">Event Name *</Label>
                  <Input
                    id="eventName"
                    type="text"
                    placeholder="November Festive Board"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    required
                  />
                </div>

                {availableCeremonies.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="ceremonyType">Ceremony Type</Label>
                    <Select value={ceremonyType} onValueChange={setCeremonyType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ceremony type (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {availableCeremonies.map((ceremony) => (
                          <SelectItem key={ceremony} value={ceremony}>
                            {ceremony}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="eventDate">Event Date *</Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eventTime">Event Time *</Label>
                    <Input
                      id="eventTime"
                      type="time"
                      value={eventTime}
                      onChange={(e) => setEventTime(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {effectiveId && (
                  <VenueSelector
                    unitId={effectiveId}
                    value={venueId}
                    onChange={(id, name) => {
                      setVenueId(id);
                      if (id !== '__custom__' && name) {
                        setLocation(name);
                      } else if (id === '__custom__') {
                        setLocation('');
                      }
                    }}
                    required={true}
                  />
                )}

                {venueId === '__custom__' && (
                  <div className="space-y-2">
                    <Label htmlFor="customLocation">Custom Location *</Label>
                    <Input
                      id="customLocation"
                      type="text"
                      placeholder="Masonic Hall, 123 High Street"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="rsvpDeadline">RSVP Deadline</Label>
                  <Input
                    id="rsvpDeadline"
                    type="date"
                    value={rsvpDeadline}
                    onChange={(e) => setRsvpDeadline(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Defaults to 7 days before event
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="maxCapacity">Maximum Capacity (Optional)</Label>
                    <Input
                      id="maxCapacity"
                      type="number"
                      min="1"
                      placeholder="120"
                      value={maxCapacity}
                      onChange={(e) => setMaxCapacity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dresscode">Dress Code</Label>
                  <Input
                    id="dresscode"
                    type="text"
                    placeholder="Black Tie"
                    value={dresscode}
                    onChange={(e) => setDresscode(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="isPublic"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    label="Make this event public"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, this event will be displayed on the Upcoming Events page on the dining homepage, making it visible to all visitors
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="requirePrefixRankUnitInfo"
                    checked={requirePrefixRankUnitInfo}
                    onChange={(e) => setRequirePrefixRankUnitInfo(e.target.checked)}
                    label={`Require Prefixes, Rank and ${unitInfoLabel} Information`}
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, attendees must provide their prefix, rank, and {unitInfoLabel} details during RSVP.
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="allowFutureEventsInterest"
                    checked={allowFutureEventsInterest}
                    onChange={(e) => setAllowFutureEventsInterest(e.target.checked)}
                    label="Allow attendees to register interest in future events"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, the RSVP form offers an &quot;Invite me to future events&quot; option that adds the attendee to this unit&apos;s guest list.
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="useCustomPrefixes"
                    checked={useCustomPrefixes}
                    onChange={(e) => setUseCustomPrefixes(e.target.checked)}
                    label="Use custom prefixes"
                  />
                  {useCustomPrefixes && (
                    <div className="ml-6 space-y-1">
                      <TagInput
                        tags={customPrefixes}
                        onChange={setCustomPrefixes}
                        placeholder="Type prefix and press Enter"
                      />
                      <p className="text-xs text-muted-foreground">
                        Press Enter after each prefix. These replace the unit&apos;s default prefix list for this event.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="guestCoversCreditCost"
                    checked={guestCoversCreditCost}
                    onChange={(e) => setGuestCoversCreditCost(e.target.checked)}
                    label="Charge a Booking Fee to the Guest to cover the Credit cost"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, a booking fee will be added per guest. 1 Credit is consumed per guest.
                  </p>
                  {guestCoversCreditCost && (
                    <div className="ml-6 mt-3 max-w-xs space-y-2">
                      <Label htmlFor="creditBookingFeePounds">Booking fee (£)</Label>
                      <p className="text-xs text-muted-foreground">
                        Per paying RSVP slot. Enter £0.00 to £5.00 (for example 0.60 for 60p).
                      </p>
                      <Input
                        id="creditBookingFeePounds"
                        type="number"
                        min={0}
                        max={MAX_CREDIT_BOOKING_FEE_PENCE / 100}
                        step={0.01}
                        value={creditBookingFeePoundsInput}
                        onChange={(e) => setCreditBookingFeePoundsInput(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Ticket Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle>Ticket Configuration</CardTitle>
                    <CardDescription>
                      Define ticket types, pricing, availability, and per-ticket course options
                    </CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addTicket}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Ticket Type
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {(Object.keys(TICKET_MODE_DESCRIPTIONS) as TicketMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setTicketMode(mode)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        ticketMode === mode ? 'border-primary bg-primary/5' : 'border-input'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-3 w-3 shrink-0 rounded-full border ${
                            ticketMode === mode ? 'border-primary bg-primary' : 'border-input'
                          }`}
                        />
                        <span className="text-sm font-medium">{TICKET_MODE_DESCRIPTIONS[mode].label}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {TICKET_MODE_DESCRIPTIONS[mode].description}
                      </p>
                    </button>
                  ))}
                </div>

                {tickets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tickets configured. Click "Add Ticket Type" to create one.</p>
                ) : (
                  tickets.map((ticket, tIndex) => (
                    <div key={ticket.id} className="rounded-lg border p-4 space-y-4">
                      <div className="mb-1 flex items-center justify-between">
                        <Label>Ticket {tIndex + 1}</Label>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeTicket(ticket.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <div className="space-y-2">
                          <Label>Ticket Name</Label>
                          <Input
                            placeholder="Standard / Adult / Child"
                            value={ticket.name}
                            onChange={(e) => updateTicketField(ticket.id, 'name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="35.00"
                            value={ticket.price}
                            onChange={(e) => updateTicketField(ticket.id, 'price', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity Available (optional)</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="e.g. 100"
                            value={ticket.totalAvailable ?? ''}
                            onChange={(e) => updateTicketField(ticket.id, 'totalAvailable', e.target.value ? parseInt(e.target.value) : undefined)}
                          />
                        </div>
                        <div className="space-y-2 flex flex-col justify-end">
                          <Label id={`restricted-${ticket.id}`}>Access</Label>
                          <Checkbox
                            id={`restricted-${ticket.id}`}
                            checked={Boolean(ticket.restricted)}
                            onChange={(e) => updateTicketField(ticket.id, 'restricted', e.target.checked)}
                            label="Restricted ticket"
                          />
                          <p className="text-xs text-muted-foreground">
                            Invited guests may only share restricted tickets they hold or were assigned
                          </p>
                        </div>
                      </div>
                      {ticketMode === 'mealChoice' && (
                        <div className="pt-2">
                          <div className="flex items-center justify-between mb-3">
                            <Label>Per-ticket Course Configuration</Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => addCourseToTicket(ticket.id)}>
                              <Plus className="mr-2 h-4 w-4" />
                              Add Course
                            </Button>
                          </div>
                          {(ticket.courses && ticket.courses.length > 0) ? (
                            (ticket.courses || []).map((course) => (
                              <div key={course.id} className="rounded-md border p-3 mb-3">
                                <div className="flex items-center justify-between mb-3">
                                  <Label>Course</Label>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeCourseFromTicket(ticket.id, course.id)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  <Input
                                    placeholder="Course name (e.g., Starter, Main, Dessert)"
                                    value={course.name}
                                    onChange={(e) => updateTicketCourse(ticket.id, course.id, 'name', e.target.value)}
                                  />
                                  <div className="space-y-1">
                                    <TagInput
                                      tags={course.options}
                                      onChange={(newOptions) => updateTicketCourse(ticket.id, course.id, 'options', newOptions)}
                                      placeholder="Type option and press comma or enter (e.g., Beef Wellington)"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Press comma or enter after each option to add it as a dining option
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-muted-foreground">No courses for this ticket type. Add courses if meal selections are required.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Questionnaire */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Additional Questions</CardTitle>
                    <CardDescription>
                      Add any additional questions for your guests. We already ask if they have special dietary requirements
                    </CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Question
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {questionnaire.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No additional questions. Click "Add Question" to add one.</p>
                ) : (
                  questionnaire.map((q, index) => (
                    <div key={q.id} className="rounded-lg border p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <Label>Question {index + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeQuestion(q.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <Input
                          placeholder="Question text"
                          value={q.question}
                          onChange={(e) => updateQuestion(q.id, 'question', e.target.value)}
                        />
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={q.type}
                          onChange={(e) => updateQuestion(q.id, 'type', e.target.value)}
                        >
                          <option value="text">Text Input</option>
                          <option value="textarea">Long Text</option>
                          <option value="select">Single Choice</option>
                          <option value="multiselect">Multiple Choice</option>
                        </select>
                        {(q.type === 'select' || q.type === 'multiselect') && (
                          <div className="space-y-1">
                            <TagInput
                              tags={q.options || []}
                              onChange={(newOptions) => updateQuestion(q.id, 'options', newOptions)}
                              placeholder="Type option and press enter (e.g., Option 1)"
                            />
                            <p className="text-xs text-muted-foreground">
                              Press enter after each option to add it
                            </p>
                          </div>
                        )}
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                          />
                          <span className="text-sm">Required</span>
                        </label>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Additional Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Instructions</CardTitle>
                <CardDescription>Any special notes for your guests</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Please arrive 15 minutes early for drinks reception..."
                  rows={5}
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating Event...' : 'Create Event (Draft)'}
              </Button>
            </div>
          </form>
        </DashboardContentContainer>
      </main>
    </div>
  );
}
